import React, { useEffect, useLayoutEffect, useRef } from "react";
import { observer } from "mobx-react-lite";
import { MdsIcon, MdsIconKind } from "@/design-system/components/icon";
import { css } from "@/domains/emotion";
import { MdsDropdownContent } from "@/design-system/components/dropdown/MdsDropdownContent";
import { UNTITLED_NOTE_TITLE } from "@/domains/untitled/untitled";
import { NoteChip } from "@/components/chips/note-chip";
import { AddToCollectionModalStore } from "@/components/modal/add-to-collection/add-to-collection-modal-store";
import { useModalTitleLimit } from "@/components/modal/modal-title-limits/modal-title-limits";
import {
  MDSModalDialog,
  MDSDialogSlotName,
  MDSText,
  MDSTextVariant,
  MDSTextField,
  MDSTextFieldSlotName,
  MDSTextFieldVariant,
} from "@/mds";
import styled from "@emotion/styled";
import { Popover } from "react-aria-components";

interface AddToCollectionModalProps {
  store: AddToCollectionModalStore;
}

export const AddToCollectionModal = observer<AddToCollectionModalProps>(({ store }) => {
  const primaryTitle = store.selectedNote?.title || UNTITLED_NOTE_TITLE;
  const secondaryTitle =
    store.selectedNoteIds.length > 1
      ? ` and ${store.selectedNoteIds.length - 1} other note${store.selectedNoteIds.length > 2 ? "s" : ""} `
      : " ";

  const title = useModalTitleLimit({ primaryTitle, secondaryTitle, isAllShared: false });
  useEffect(() => {
    if (store.isOpen) {
      store.setSearchQuery("");
    }
  }, [secondaryTitle, store.isOpen, title, store]);

  return (
    <MDSModalDialog isOpen={store.isOpen} handleClose={() => store.close()}>
      <MDSText slot={MDSDialogSlotName.Header} variant={MDSTextVariant.HeaderL}>
        Organize <NoteChip inline title={title} /> {secondaryTitle}
      </MDSText>

      <div slot={MDSDialogSlotName.Content} onKeyDown={store.onKeyDown}>
        <CollectionsSearchField store={store} />
      </div>
    </MDSModalDialog>
  );
});

const CollectionsSearchField = observer<AddToCollectionModalProps>(({ store }) => {
  const inputRef = useRef<HTMLInputElement>(null);
  const triggerRef = useRef<HTMLDivElement>(null);
  useLayoutEffect(() => inputRef.current?.focus(), []);
  return (
    <>
      <CollectionSearchField ref={triggerRef}>
        <MDSTextField
          variant={MDSTextFieldVariant.Form}
          ref={inputRef}
          value={store.searchQuery}
          onChange={value => store.setSearchQuery(value)}
          placeholder="Add to existing or new collection"
        >
          <MdsIcon slot={MDSTextFieldSlotName.LeftAddon} kind={MdsIconKind.Search} />
        </MDSTextField>
        <StyledPopover
          triggerRef={triggerRef}
          isOpen={store.dropdownContentList.items.length > 0}
          placement="bottom start"
          isNonModal
        >
          <MdsDropdownContent
            contentList={store.dropdownContentList}
            className={dropdownWidthStyles}
          />
        </StyledPopover>
      </CollectionSearchField>
    </>
  );
});

const dropdownWidthStyles = css({
  width: "100%",
});

const CollectionSearchField = styled.div`
  position: relative;
`;

const StyledPopover = styled(Popover)`
  width: min(600px, calc(90vw - 40px));
`;
